import { useState, useEffect } from 'react';

const navItems = [
  { label: '選ばれる理由', href: '#why' },
  { label: '事業内容', href: '#business' },
  { label: '代表メッセージ', href: '#message' },
  { label: '会社概要', href: '#company' },
];

export default function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 pointer-events-none pt-4 px-4 sm:px-6 md:px-8 transition-all duration-500">
      {/* ピル型ナビ：中央に浮かぶ角丸バー */}
      <div
        className="max-w-4xl mx-auto pointer-events-auto overflow-hidden rounded-3xl transition-shadow duration-300 ease-out md:rounded-full"
        style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid rgba(168,205,217,0.35)',
          boxShadow: scrolled
            ? '0 12px 40px rgba(11, 26, 42, 0.12), 0 2px 8px rgba(0, 0, 0, 0.05)'
            : '0 8px 32px rgba(11, 26, 42, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
        }}
      >
      <nav className="px-4 sm:px-6 lg:px-10 h-16 sm:h-[72px] flex items-center justify-between">
        {/* Logo */}
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          className="flex items-center cursor-pointer"
          aria-label="株式会社Universe トップへ"
        >
          <img
            src="https://storage.readdy-site.link/project_files/2eebd179-5ab9-4607-b0cd-6c7581f8e0aa/3d2cf485-900e-4930-b64e-e729cce20055_Universe.png?v=1cd2231bd8f54e77057a499a35c5ec79"
            alt="株式会社Universe ロゴ"
            className="w-auto object-contain h-10 sm:h-14"
            style={{
              display: 'block',
              border: 'none',
              boxShadow: 'none',
            }}
          />
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-4">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={(e) => { e.preventDefault(); handleNavClick(item.href); }}
              className="nav-link text-sm font-light tracking-wider cursor-pointer whitespace-nowrap"
              style={{ color: '#2C2C2C', fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              {item.label}
            </a>
          ))}
          <a
            href="#entry"
            onClick={(e) => { e.preventDefault(); handleNavClick('#entry'); }}
            className="btn-pill-outline px-6 py-2 text-sm font-medium whitespace-nowrap cursor-pointer"
            style={{
              color: '#B89B4A',
              border: '1px solid #B89B4A',
              letterSpacing: '0.08em',
              fontFamily: "'Noto Sans JP', sans-serif",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'rgba(184,155,74,0.12)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
            }}
          >
            エントリー・お問い合わせ
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          type="button"
          className="md:hidden flex flex-col justify-center gap-1.5 p-2 cursor-pointer [-webkit-tap-highlight-color:transparent]"
          onClick={() => setMenuOpen((o) => !o)}
          aria-expanded={menuOpen}
          aria-controls="mobile-nav-panel"
          aria-label={menuOpen ? 'メニューを閉じる' : 'メニューを開く'}
        >
          <span
            className="block h-0.5 w-6 origin-center transition-[transform,opacity] duration-300 ease-out motion-reduce:transition-none"
            style={{
              backgroundColor: '#2C2C2C',
              transform: menuOpen ? 'translateY(8px) rotate(45deg)' : 'none',
            }}
          />
          <span
            className="block h-0.5 w-6 transition-opacity duration-300 ease-out motion-reduce:transition-none"
            style={{ backgroundColor: '#2C2C2C', opacity: menuOpen ? 0 : 1 }}
          />
          <span
            className="block h-0.5 w-6 origin-center transition-[transform,opacity] duration-300 ease-out motion-reduce:transition-none"
            style={{
              backgroundColor: '#2C2C2C',
              transform: menuOpen ? 'translateY(-8px) rotate(-45deg)' : 'none',
            }}
          />
        </button>
      </nav>

      {/* モバイル：grid-template-rows で高さを滑らかに開閉（max-height より安定） */}
      <div
        id="mobile-nav-panel"
        className={`md:hidden grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none ${
          menuOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="min-h-0 overflow-hidden">
        <div
          className="flex flex-col gap-5 border-t border-solid px-4 pb-6 pt-2 sm:px-6"
          style={{
            // 開閉で border の有無を切り替えない（1px 常に確保してレイアウトの跳ねを防ぐ）
            borderTopColor: menuOpen ? 'rgba(168, 205, 217, 0.25)' : 'transparent',
          }}
        >
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={(e) => { e.preventDefault(); handleNavClick(item.href); }}
              className="text-sm font-light py-2 border-b cursor-pointer"
              style={{
                color: '#2C2C2C',
                borderColor: 'rgba(168,205,217,0.3)',
                letterSpacing: '0.05em',
              }}
            >
              {item.label}
            </a>
          ))}
          <a
            href="#entry"
            onClick={(e) => { e.preventDefault(); handleNavClick('#entry'); }}
            className="btn-pill-outline mt-2 px-6 py-3 text-sm font-medium text-center whitespace-nowrap cursor-pointer"
            style={{
              color: '#B89B4A',
              border: '1px solid #B89B4A',
            }}
          >
            エントリー・お問い合わせ
          </a>
        </div>
        </div>
      </div>
      </div>
    </header>
  );
}
